import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";

import { HelperTooltip } from "../../../components/ui-kit/base/helper-tooltip";
import { Checkbox } from "../../../components/ui-kit/fields/checkbox";
import { DateInput } from "../../../components/ui-kit/fields/date-input";
import { FormRow } from "../../../components/ui-kit/form/form-row";

/**
 * The Started / Ended date rows of the add medication form. A medication is
 * open-ended until explicitly marked as ended, so the end date is gated behind
 * a checkbox on the Started row — and it is the checkbox, not whatever value
 * the date input happens to still hold, that decides whether an end date is
 * submitted (see {@link endedDateToSubmit}).
 */
export const MedicationPeriodFields = observer(function MedicationPeriodFields({
  model,
}: {
  model: MedicationPeriod;
}) {
  const { t } = useLingui();
  const setHasEnded = (hasEnded: boolean) => {
    model.medication_has_ended = hasEnded;
  };

  return (
    <>
      <FormRow
        label={t`Started`}
        data={
          <MedicationStartedField
            model={model}
            hasEnded={model.medication_has_ended}
            onHasEndedChange={setHasEnded}
          />
        }
      />
      <FormRow
        label={t`Ended`}
        data={
          <MedicationEndedField
            model={model}
            hasEnded={model.medication_has_ended}
            onHasEndedChange={setHasEnded}
          />
        }
      />
    </>
  );
});

/**
 * The Started date paired with the "Has ended" checkbox that gates the end
 * date. Shared with the medications table's edit dialog, which lays the same
 * two fields out as table columns; the forms differ only in where the checkbox
 * state lives, so it is passed in rather than bound to the model.
 */
export const MedicationStartedField = observer(function MedicationStartedField({
  model,
  hasEnded,
  onHasEndedChange,
}: MedicationDateFieldProps) {
  const { t } = useLingui();
  return (
    <span className="flex items-center gap-3">
      <DateInput model={model} attr="medication_started" type="date" />
      <span className="flex items-center gap-1">
        <Checkbox
          label={t`Has ended`}
          value={hasEnded}
          onChange={onHasEndedChange}
        />
        <HelperTooltip content={hasEndedHint} />
      </span>
    </span>
  );
});

/** The end date, editable only once "Has ended" is ticked. */
export const MedicationEndedField = observer(function MedicationEndedField({
  model,
  hasEnded,
  onHasEndedChange,
}: MedicationDateFieldProps) {
  return (
    <DateInput
      model={model}
      attr="medication_ended"
      type="date"
      // Editing the date pins the checkbox on. In the edit dialog `hasEnded`
      // falls back to the draft's own end date until the checkbox is touched
      // (see {@link draftHasEnded}), so clearing the field to retype it would
      // otherwise untick the box and disable the input mid-edit.
      onChange={() => onHasEndedChange(true)}
      disabled={!hasEnded}
      // Ticking the box without picking a date has nothing to submit — the
      // form would close as if it had saved, leaving the medication ongoing.
      // Block the submit instead. Only meaningful while enabled, which is
      // exactly when the box is ticked.
      required={hasEnded}
    />
  );
});

/**
 * Shared by both forms so the explanation can't drift between them. States the
 * part that isn't visible from the control itself: the checkbox, not the date
 * input's contents, is what decides whether an end date is saved.
 */
export const hasEndedHint = (
  <Trans>
    Unlocks the <strong>Ended</strong> date. While unticked the medication is
    saved as ongoing — with no end date, not even one still shown in the
    greyed-out field.
  </Trans>
);

type MedicationDateFieldProps = {
  model: MedicationDates;
  hasEnded: boolean;
  onHasEndedChange: (hasEnded: boolean) => void;
};

type MedicationDates = {
  medication_started: string | null;
  medication_ended: string | null;
};

export type MedicationPeriod = MedicationDates & {
  /**
   * UI-only: whether the medication has an end date. Not part of any payload —
   * it gates `medication_ended` instead.
   */
  medication_has_ended: boolean;
};

/**
 * The end date to submit. Unchecking "Has ended" leaves the date input's value
 * in place (so re-checking restores it) but must not submit it, and must clear
 * an end date the record already had — hence `null` rather than omitting it.
 */
export function endedDateToSubmit(model: MedicationPeriod) {
  return model.medication_has_ended ? model.medication_ended : null;
}

/**
 * The edit dialog builds its draft from the table's columns, so it has no way to
 * seed a key that isn't one. Instead the checkbox stays absent from the draft
 * until toggled and falls back to whether the record already has an end date —
 * which is what the draft's own end date still reads as at that point.
 */
export function draftHasEnded(
  draft: Record<string, unknown>,
  endedDate: unknown,
) {
  return (draft[HAS_ENDED_KEY] as boolean | undefined) ?? endedDate != null;
}

export function setDraftHasEnded(
  draft: Record<string, unknown>,
  hasEnded: boolean,
) {
  draft[HAS_ENDED_KEY] = hasEnded;
}

/**
 * Applies the checkbox's meaning to an edit patch and drops the UI-only key, so
 * it never reaches the API. Absent means the user never touched the checkbox,
 * which leaves the end date to the normal changed-fields logic.
 */
export function applyHasEndedToPatch(patch: Record<string, unknown>) {
  const { [HAS_ENDED_KEY]: hasEnded, ...rest } = patch;
  return hasEnded === false ? { ...rest, medication_ended: null } : rest;
}

const HAS_ENDED_KEY = "medication_has_ended";
